import {
  getLegacyCreditIdentity,
  resolveLegacyCreditFlow,
  resolveLegacyCreditModalSetting,
} from './creditFlowResolver';
import {
  LEGACY_PAYMENT_ORIGINS,
  recordLegacySupheroCreditHistory,
  startLegacyCreditCheckout,
} from './creditProviderGateway';

const TIVIGO_APP_ENDPOINT = process.env.REACT_APP_TIVIGO_APP_ENDPOINT
  || 'https://tivigo.store/app-endpoint.php';

// Bu anahtar bir ödeme sağlayıcısı sırrı değildir; yalnız mevcut süre/paket
// satın alma çağrısının app-endpoint.php istemci kimliğidir.
const TIVIGO_APP_CLIENT_KEY = process.env.REACT_APP_TIVIGO_APP_CLIENT_KEY
  || 'tivigo_app_2026_7f3a9c2e1b8d4f6a0e5c9b2d8f4a6e0c1b7d3f9a5e2c8b4d6f0a3e9c5b1d7f3a6e0c4b8d2f5a9e1c7b3d6f0a4e8c2b5d9f3a7e0c4b8d1f6a3e9c5b2d8f0a4e6c1b7d3f9a5e2c8b4d6f0a3e9c5b1d7f3a6e0c4b8d2f5a9e1c7b3d6f0a4e8c2b5d9f3a7e0c4b8d1f6a3e9c5b2d8f0a4e6c1b7d3f9a5e2c8b4';

const CHECKOUT_TIMEOUT_MS = 45000;
const TIVIGO_CRYPTO_MERCHANT_ORDER_PATTERN = /^tivigo-credit-\d+-[a-f0-9]{32}$/;

export const TIVIGO_PAYMENT_ORIGINS = LEGACY_PAYMENT_ORIGINS;

export const shouldRecordLegacyCreditResult = (result) => (
  result?.success === true && result?.provider !== 'payizone-crypto'
);

const readStoredCredentials = () => {
  if (typeof window === 'undefined') return {};

  try {
    return JSON.parse(window.localStorage.getItem('xtream_credentials') || '{}');
  } catch (error) {
    return {};
  }
};

const getCreditSession = (userInfo) => {
  const info = userInfo?.user_info || {};
  const stored = readStoredCredentials();
  const username = String(info.username || stored.username || '').trim();
  const password = String(info.password || stored.password || '');
  const passwordIsMasked = /^[*•]+$/.test(password.trim());
  const isThemeDemo = username === 'demo_user' && password === 'demo-password';

  if (!username || !password || passwordIsMasked) {
    throw new Error('Bu işlem için geçerli kullanıcı adı ve şifreyle oturum açın.');
  }

  if (isThemeDemo) {
    throw new Error('Tema demo hesabı gerçek bir işlem başlatamaz. Canlı kullanıcı hesabıyla deneyin.');
  }

  return { username, password };
};

const fetchWithTimeout = async (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw new Error('Ödeme sunucusu zamanında yanıt vermedi. Lütfen tekrar deneyin.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const postAppEndpoint = async (endpoint, body) => {
  const url = new URL(TIVIGO_APP_ENDPOINT);
  url.searchParams.set('endpoint', endpoint);
  url.searchParams.set('client_key', TIVIGO_APP_CLIENT_KEY);

  const response = await fetchWithTimeout(url.toString(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }, CHECKOUT_TIMEOUT_MS);

  let payload;
  try {
    payload = await response.json();
  } catch (error) {
    throw new Error('Ödeme sunucusunun yanıtı okunamadı.');
  }

  if (!response.ok || payload?.success !== true || !payload?.data) {
    const requestError = new Error(payload?.message || 'Ödeme işlemi başlatılamadı.');
    requestError.code = String(payload?.data?.code || payload?.code || '').trim();
    requestError.status = response.status;
    throw requestError;
  }

  return payload.data;
};

export const createTivigoCryptoRequestId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `tivigo_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  }

  // Bu deger bir secret degil, yalniz tekrar eden siparis isteklerini eslestirir.
  return `tivigo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
};

const normalizeCryptoCreditAmount = (value) => {
  const amount = Number(value);
  const rounded = Math.round(amount * 100) / 100;
  if (!Number.isFinite(amount)
    || amount < 50
    || amount > 100000
    || Math.abs(amount - rounded) > Number.EPSILON * 100) {
    throw new Error('50 TL ile 100.000 TL arasında, en fazla iki ondalıklı bir tutar girin.');
  }
  return rounded.toFixed(2);
};

export const startTivigoCryptoCreditCheckout = async ({
  userInfo,
  amount,
  requestId,
}) => {
  const session = getCreditSession(userInfo);
  const normalizedRequestId = String(requestId || '').trim();
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{15,127}$/.test(normalizedRequestId)) {
    throw new Error('Ödeme isteği kimliği oluşturulamadı. Sayfayı yenileyip tekrar deneyin.');
  }

  const data = await postAppEndpoint('payizone_crypto_session_create', {
    ...session,
    amount: normalizeCryptoCreditAmount(amount),
    request_id: normalizedRequestId,
  });

  return {
    checkoutUrl: data.checkout_url,
    orderId: data.order_id,
    merchantOrderId: data.merchant_order_id,
    provider: data.provider || 'payizone-crypto',
  };
};

export const getTivigoCryptoCreditCheckoutStatus = async ({
  userInfo,
  merchantOrderId,
}) => {
  const session = getCreditSession(userInfo);
  const normalizedMerchantOrderId = String(merchantOrderId || '').trim();
  if (!TIVIGO_CRYPTO_MERCHANT_ORDER_PATTERN.test(normalizedMerchantOrderId)) {
    throw new Error('Kripto ödeme kaydı doğrulanamadı.');
  }

  const data = await postAppEndpoint('payizone_crypto_session_status', {
    ...session,
    merchant_order_id: normalizedMerchantOrderId,
  });
  const amount = Number(data.amount);
  return {
    merchantOrderId: String(data.merchant_order_id || ''),
    orderId: String(data.order_id || ''),
    amount: Number.isFinite(amount) && amount > 0 ? amount : null,
    currency: String(data.currency || '').trim().toUpperCase(),
    status: String(data.status || '').trim().toLowerCase(),
    paymentStatus: String(data.payment_status || '').trim().toLowerCase(),
    credited: data.credited === true,
    terminal: data.terminal === true,
    provider: 'payizone-crypto',
  };
};

export const startTivigoCreditCheckout = async ({
  userInfo,
  amount,
  provider,
  flow,
  userId,
  userEmail,
  uiDark = false,
}) => {
  const identity = getLegacyCreditIdentity(userInfo);
  const resolvedFlow = flow || (provider
    ? {
      ...resolveLegacyCreditModalSetting(provider),
      ...identity,
      setting: provider,
    }
    : await resolveLegacyCreditFlow({ userInfo }));

  return startLegacyCreditCheckout({
    flow: resolvedFlow,
    amount,
    userId: userId || resolvedFlow.userId || identity.userId,
    userEmail: userEmail || resolvedFlow.userEmail || identity.userEmail,
    uiDark,
  });
};

export const confirmTivigoCreditCheckout = async ({ amount, provider, flow }) => (
  recordLegacySupheroCreditHistory({
    flow: flow || resolveLegacyCreditModalSetting(provider),
    amount,
    provider,
  })
);

export const fetchTivigoCreditBalance = async () => {
  const token = typeof window !== 'undefined'
    ? window.localStorage.getItem('xtream_api_token') || ''
    : '';
  if (!token) throw new Error('Mevcut kredi bilgisi alınamadı.');

  const response = await fetch(
    `https://tivigo.store/endpoint.php?endpoint=user_data&token=${token}`,
    { method: 'GET' },
  );
  const payload = await response.json();
  const credit = Number(payload?.data?.user?.kredi);

  if (!response.ok || !payload?.success || !Number.isFinite(credit)) {
    throw new Error(payload?.message || 'Mevcut kredi bilgisi alınamadı.');
  }

  return credit;
};

export const purchaseTivigoPackage = async ({
  userInfo,
  duration,
  maxConnections,
  couponCode = '',
  pricing,
}) => {
  const session = getCreditSession(userInfo);
  const normalizedMaxConnections = Math.min(
    2,
    Math.max(1, Math.trunc(Number(maxConnections) || 1)),
  );

  return postAppEndpoint('purchase_package', {
    ...session,
    duration: Number(duration),
    max_connections: normalizedMaxConnections,
    coupon_code: String(couponCode || '').trim(),
    ...(pricing ? { pricing_revision: pricing.revision, pricing_currency: pricing.currency } : {}),
  });
};
